import React, { useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { cn } from '@src/utils/ui';
import { Button } from '@src/components/ui/button/button';
import { useValidateGovernmentId } from '../../api/validateGovernmentId';
import { getDocusignMaxAttemptsFromCustomField } from '@src/modules/user/util/CustomField';
import { getLatestMediaUrl } from '@src/utils/getLatestMediaUrl';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import PropTypes from 'prop-types';
import { usePreventNavigationContext } from '@src/components/hooks/usePreventNavigationNative';
import { GovIdVerifiedBadge } from '@src/components/common/gov-id-verified-badge/gov-id-verified-badge';
import { ConfirmIdentityForm } from './confirm-identity-form';
import { InfoCallout } from '@src/components/common/callouts/info-callout/info-callout';

const successElement = (
    <div className='tw-flex tw-items-center tw-gap-[12px] tw-p-[16px] tw-font-[500] tw-bg-[#6DAE4329]  tw-rounded-[8px]'>
        <img src='/assets/images/check-circle-green.svg' title='criminal record checked' alt='check-circle' />
        Your ID is has been verified.
    </div>
);

const HelpIcon = () => (
    <div className='tw-col-span-2'>
        <div className='tw-ml-auto tw-rounded-full tw-aspect-square sm:tw-absolute tw-top-0 tw-right-0 tw-shadow-lg tw-flex tw-items-center tw-justify-center tw-w-14'>
            <HelpOutlineOutlinedIcon className='tw-text-4xl' />
        </div>
    </div>
);

const getVeryIdContent = ({ governmentId, error, isVerifyAllowed, GovIdProgress, description }) => {
    if (GovIdProgress === 'undergone_docusign') {
        return null;
    }

    if (!isVerifyAllowed) {
        return (
            <>
                <p className='tw-col-span-2 tw-text-primary-500 tw-font-bold tw-text-[20px]'>
                    Government ID Verification Unexpected Status
                </p>
                <p className='tw-col-span-2'>
                    If you're experiencing any difficulties, please reach out to our support team by emailing us at
                    support@marketplace.bcaa.com and one of our support team members will get back to you.
                </p>
                {/* <HelpIcon /> */}
            </>
        );
    }

    if (governmentId === 'None' || !governmentId) {
        return (
            <>
                <p className='tw-col-span-2'>
                    Help us create a trusted community by verifying your identity. Once complete, a blue verification
                    badge will appear next to your name.
                </p>
                <div>
                    <p>$6.50</p>
                    <p>Excluding taxes</p>
                </div>
                {/* <HelpIcon /> */}
            </>
        );
    }

    if (error) {
        return (
            <>
                <p className='tw-col-span-2 tw-text-primary-500 tw-font-bold tw-text-[20px]'>
                    Government ID Verification Unexpected Status
                </p>
                <p className='tw-col-span-2'>
                    An error has occurred attempting to validate your Government ID, you may try again or contact us for
                    further assistance.
                </p>
                {/* <HelpIcon /> */}
            </>
        );
    }

    if (governmentId === 'Purchased') {
        return (
            <>
                <p className='tw-col-span-2'>
                    {description ||
                        `Thank you for purchasing ID Verification. To complete the process, please follow the steps in DocuSign. Please note the first and last name on your BCAA Task Marketplace account must match the name on your government-issued ID. If your account details need updating, please make the changes on your Profile page before starting the ID Verification process.`}
                </p>
                {/* <HelpIcon /> */}
            </>
        );
    }

    return null;
};

const getVerifyIdButton = ({
    governmentId,
    handlePayment,
    handleVerify,
    isLoading,
    isVerifyAllowed,
    GovIdProgress,
    disabled
}) => {
    if (governmentId === 'None' || !governmentId) {
        return (
            <Button
                variant='contained'
                endIcon={<OpenInNewIcon />}
                className={cn([disabled ? '' : '!tw-bg-[#00B451] hover:!tw-bg-[#00B451]'])}
                onClick={handlePayment}
                disabled={disabled}
            >
                PAY
            </Button>
        );
    }

    if (governmentId === 'Purchased') {
        if (GovIdProgress === 'undergone_docusign') {
            return (
                <InfoCallout>
                    ID verification is in progress. Please refresh the page to see the latest status.
                </InfoCallout>
            );
        }

        if (!isVerifyAllowed) {
            return (
                <div className='tw-flex tw-items-center tw-gap-[12px] tw-p-[16px] tw-font-[500] tw-bg-[#F4511E29] tw-rounded-[8px]'>
                    <img src='/assets/images/x-octagon.svg' title='criminal record checked' alt='check-circle' />
                    Error, contact BCAA Customer Support.{' '}
                    <a href='/pages/contact-us' target='_blank'>
                        Contact us
                    </a>
                </div>
            );
        }

        return (
            <Button variant='contained' endIcon={<OpenInNewIcon />} onClick={handleVerify} disabled={disabled}>
                {isLoading ? 'Loading...' : 'Verify ID'}
            </Button>
        );
    }

    if (governmentId === 'Verified') {
        return successElement;
    }

    return null;
};

export const GovernmentIdVerification = props => {
    const { user } = props;
    // eslint-disable-next-line no-unused-vars
    const src = getLatestMediaUrl(user); // Currently unused but available for future avatar display
    const [openConfirmIdentityForm, setOpenConfirmIdentityForm] = useState(false);

    const [envelopeId] = useState(
        user.CustomFields.find(customField => customField.Code.includes('-EnvelopeID-'))?.Values[0]
    );
    const [isVerifyAllowed] = useState(getDocusignMaxAttemptsFromCustomField(user.CustomFields));
    const [error, setError] = useState(null);
    const { enableNavigation } = usePreventNavigationContext();

    const validateGovnermentIdMutation = useValidateGovernmentId();

    const handlePayment = () => {
        enableNavigation();
        window.location.href = `${process.env.BUYER_ID_VERIFICATION_PAYMENT_URL}?client_reference_id=${user.ID}`;
    };

    const handleVerify = async () => {
        setOpenConfirmIdentityForm(true);
    };

    const doVerify = async ({ firstName, lastName }) => {
        try {
            const params = {
                signerEmail: user.Email,
                signerName: `${firstName} ${lastName}`,
                clientUserId: user.ID,
                dsReturnUrl: window.location.href,
                envelopeId: envelopeId ?? null
            };

            const response = await validateGovnermentIdMutation.mutateAsync(params);
            if (!response.data.ok) {
                setError(true);
            }

            enableNavigation();
            if (props.onVerify) props.onVerify();
            if (response.data.redirectUrl) {
                window.location.href = response.data.redirectUrl;
            }
        } catch (err) {
            setError(true);
            console.error('Verification error:', err);
        }
    };

    const verifyContent = getVeryIdContent({
        governmentId: user.GovernmentID,
        error,
        isVerifyAllowed,
        GovIdProgress: user.GovIdProgress,
        description: props.description
    });

    const verifyButton = getVerifyIdButton({
        governmentId: user.GovernmentID,
        handlePayment,
        handleVerify,
        isLoading: validateGovnermentIdMutation.isPending,
        isVerifyAllowed,
        GovIdProgress: user.GovIdProgress,
        disabled: props.disabled
    });

    return (
        <div
            className={cn([
                'tw-space-y-[16px]',
                user.GovernmentID === 'Verified' || user.GovIdProgress === 'undergone_docusign'
                    ? ''
                    : 'tw-bg-[#E8F0FC] tw-p-[6px]'
            ])}
        >
            <div className='tw-font-[500]'>{props.header || 'ID verification'}</div>
            {verifyContent}
            <GovIdVerifiedBadge className='tw-w-[24px] tw-aspect-square' />
            {verifyButton}
            <ConfirmIdentityForm
                open={openConfirmIdentityForm}
                onClose={() => setOpenConfirmIdentityForm(false)}
                onSubmit={doVerify}
                defaultValues={{
                    firstName: user.FirstName,
                    lastName: user.LastName
                }}
            />
        </div>
    );
};

GovernmentIdVerification.propTypes = {
    user: PropTypes.shape({
        ID: PropTypes.string.isRequired,
        Email: PropTypes.string.isRequired,
        FirstName: PropTypes.string,
        LastName: PropTypes.string,
        GovernmentID: PropTypes.string,
        GovIdProgress: PropTypes.string,
        CustomFields: PropTypes.arrayOf(
            PropTypes.shape({
                Code: PropTypes.string.isRequired,
                Values: PropTypes.arrayOf(PropTypes.string).isRequired
            })
        ).isRequired,
        Media: PropTypes.arrayOf(
            PropTypes.shape({
                MediaUrl: PropTypes.string
            })
        )
    }).isRequired,
    description: PropTypes.string,
    disabled: PropTypes.bool,
    header: PropTypes.string,
    onVerify: PropTypes.func
};
